import React, { useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'

import { GuestCardPremiumQuestion } from '../Outbound/Campaign/GuestCards/GuestCardPremiumQuestion'
import { GuestCardPremiumOption } from '../../models/Outreach/types'
import { WorkbookSectionFieldDraft } from './workbookSectionDraft'

const HAS_PETS_HELPER_TEXT =
    'Do not include service or assistance animals. Those are handled separately.'

const useStyles = makeStyles((theme: Theme) => ({
    preview: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        '& > .MuiFormControl-root > .MuiInputLabel-root, & > .MuiFormControl-root > .MuiFormLabel-root, & > .MuiFormLabel-root, & > .MuiFormControlLabel-root > .MuiFormControlLabel-label': {
            fontWeight: 700,
        },
    },
    hint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
        marginTop: theme.spacing(0.5),
    },
    roommateCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.5),
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    roommateTitle: {
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    addBtn: {
        alignSelf: 'flex-start',
        textTransform: 'none',
    },
    questionLabel: {
        fontWeight: 700,
    },
    premiumsQuestion: {
        '& > .MuiFormLabel-root': {
            fontWeight: 700,
        },
    },
}))

export type FloorPlanPreviewOption = {
    id: number
    name: string
    bed_count?: number | null
    bath_count?: number | null
}

export type PetPreviewOption = {
    id: string
    label: string
}

function floorPlanOptionLabel(option: FloorPlanPreviewOption): string {
    const beds = option.bed_count != null ? `${option.bed_count} bed` : null
    const baths = option.bath_count != null ? `${option.bath_count} bath` : null
    const detail = [beds, baths].filter(Boolean).join(' / ')
    return detail ? `${option.name} (${detail})` : option.name
}

type RoommateDraft = { name: string; email: string; phone: string }

export const WorkbookFieldPreview: React.FC<{
    field: WorkbookSectionFieldDraft
    required: boolean
    floorPlanOptions?: FloorPlanPreviewOption[]
    premiumOptions?: GuestCardPremiumOption[]
    animalTypeOptions?: PetPreviewOption[]
    dogBreedOptions?: PetPreviewOption[]
}> = ({
    field,
    required,
    floorPlanOptions = [],
    premiumOptions = [],
    animalTypeOptions = [],
    dogBreedOptions = [],
}) => {
    const classes = useStyles()
    const label = field.question_text
    const questionLabelProps = { className: classes.questionLabel }
    const [textValue, setTextValue] = useState('')
    const [selectValue, setSelectValue] = useState('')
    const [boolValue, setBoolValue] = useState('')
    const [checked, setChecked] = useState(false)
    const [premiumIds, setPremiumIds] = useState<number[]>([])
    const [premiumsNone, setPremiumsNone] = useState(false)
    const [roommates, setRoommates] = useState<RoommateDraft[]>([
        { name: '', email: '', phone: '' },
    ])

    if (field.field_id === 'floor_plan_preference') {
        return (
            <Box className={classes.preview}>
                <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    required={required}
                >
                    <InputLabel className={classes.questionLabel}>
                        {label}
                    </InputLabel>
                    <Select
                        value={selectValue}
                        onChange={(e) =>
                            setSelectValue(e.target.value as string)
                        }
                        label={label}
                    >
                        <MenuItem value="">
                            <em>Select…</em>
                        </MenuItem>
                        {floorPlanOptions.map((config) => (
                            <MenuItem key={config.id} value={String(config.id)}>
                                {floorPlanOptionLabel(config)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {floorPlanOptions.length === 0 ? (
                    <Typography className={classes.hint}>
                        Uses this property&apos;s unit types. None are available
                        on the current property yet.
                    </Typography>
                ) : null}
            </Box>
        )
    }

    if (field.field_id === 'pet_animal_type') {
        return (
            <Box className={classes.preview}>
                <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={label}
                    value={selectValue}
                    onChange={(e) => setSelectValue(e.target.value)}
                    required={required}
                    fullWidth
                    InputLabelProps={questionLabelProps}
                    helperText="Choose from the animal types this property allows."
                >
                    <MenuItem value="">
                        <em>Select…</em>
                    </MenuItem>
                    {animalTypeOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                {animalTypeOptions.length === 0 ? (
                    <Typography className={classes.hint}>
                        Uses this property&apos;s allowed animal types from
                        Property Data. None are configured yet.
                    </Typography>
                ) : null}
            </Box>
        )
    }

    if (field.field_id === 'pet_breed') {
        return (
            <Box className={classes.preview}>
                <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={label}
                    value={selectValue}
                    onChange={(e) => setSelectValue(e.target.value)}
                    required={required}
                    fullWidth
                    InputLabelProps={questionLabelProps}
                    helperText="If this breed is not listed, it is not an allowed breed at this property."
                >
                    <MenuItem value="">
                        <em>Select…</em>
                    </MenuItem>
                    {dogBreedOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                {dogBreedOptions.length === 0 ? (
                    <Typography className={classes.hint}>
                        Uses this property&apos;s allowed dog breeds from
                        Property Data. None are configured yet.
                    </Typography>
                ) : null}
            </Box>
        )
    }

    if (field.field_id === 'preferred_premiums') {
        return (
            <Box className={classes.preview}>
                <GuestCardPremiumQuestion
                    className={classes.premiumsQuestion}
                    label={label}
                    premiums={premiumOptions}
                    selectedIds={premiumIds}
                    noneSelected={premiumsNone}
                    required={required}
                    onChange={({ selectedIds, noneSelected }) => {
                        setPremiumIds(selectedIds)
                        setPremiumsNone(noneSelected)
                    }}
                />
                {premiumOptions.length === 0 ? (
                    <Typography className={classes.hint}>
                        Uses this property&apos;s premiums. None are configured
                        yet.
                    </Typography>
                ) : null}
            </Box>
        )
    }

    if (field.field_id === 'preferred_roommates') {
        return (
            <Box className={classes.preview}>
                <FormLabel component="legend" className={classes.questionLabel}>
                    {label}
                    {required ? ' *' : ''}
                </FormLabel>
                {roommates.map((roommate, index) => (
                    <Box key={index} className={classes.roommateCard}>
                        <Typography className={classes.roommateTitle}>
                            Roommate {index + 1}
                        </Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            label="Name"
                            value={roommate.name}
                            onChange={(e) => {
                                const next = [...roommates]
                                next[index] = {
                                    ...next[index],
                                    name: e.target.value,
                                }
                                setRoommates(next)
                            }}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            label="Email"
                            type="email"
                            value={roommate.email}
                            onChange={(e) => {
                                const next = [...roommates]
                                next[index] = {
                                    ...next[index],
                                    email: e.target.value,
                                }
                                setRoommates(next)
                            }}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            label="Phone"
                            type="tel"
                            value={roommate.phone}
                            onChange={(e) => {
                                const next = [...roommates]
                                next[index] = {
                                    ...next[index],
                                    phone: e.target.value,
                                }
                                setRoommates(next)
                            }}
                            fullWidth
                        />
                    </Box>
                ))}
                <Button
                    className={classes.addBtn}
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                        setRoommates([
                            ...roommates,
                            { name: '', email: '', phone: '' },
                        ])
                    }
                >
                    Add another roommate
                </Button>
            </Box>
        )
    }

    if (
        field.field_id === 'no_legal_middle_name' ||
        field.field_id === 'no_guarantor_middle_name'
    ) {
        return (
            <FormControlLabel
                classes={{ label: classes.questionLabel }}
                control={
                    <Checkbox
                        color="primary"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                    />
                }
                label={label}
            />
        )
    }

    if (field.input_type === 'boolean') {
        return (
            <TextField
                select
                size="small"
                variant="outlined"
                label={label}
                value={boolValue}
                onChange={(e) => setBoolValue(e.target.value)}
                required={required}
                fullWidth
                InputLabelProps={questionLabelProps}
                helperText={
                    field.field_id === 'has_pets'
                        ? HAS_PETS_HELPER_TEXT
                        : undefined
                }
            >
                <MenuItem value="">
                    <em>Select…</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </TextField>
        )
    }

    if (field.input_type === 'date') {
        return (
            <TextField
                size="small"
                variant="outlined"
                type="date"
                label={label}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                required={required}
                fullWidth
                InputLabelProps={{ shrink: true, ...questionLabelProps }}
            />
        )
    }

    if (field.input_type === 'select') {
        const options = field.options || []
        return (
            <TextField
                select
                size="small"
                variant="outlined"
                label={label}
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                required={required}
                fullWidth
                InputLabelProps={questionLabelProps}
            >
                <MenuItem value="">
                    <em>Select…</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>
        )
    }

    if (field.input_type === 'currency') {
        return (
            <TextField
                size="small"
                variant="outlined"
                type="number"
                label={label}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                required={required}
                fullWidth
                InputLabelProps={questionLabelProps}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                    ),
                }}
            />
        )
    }

    if (field.input_type === 'file') {
        return (
            <TextField
                size="small"
                variant="outlined"
                type="file"
                label={label}
                required={required}
                fullWidth
                InputLabelProps={{ shrink: true, ...questionLabelProps }}
            />
        )
    }

    if (field.input_type === 'address') {
        return (
            <TextField
                size="small"
                variant="outlined"
                label={label}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                required={required}
                fullWidth
                InputLabelProps={questionLabelProps}
                placeholder="Street, city, state, ZIP"
            />
        )
    }

    return (
        <TextField
            size="small"
            variant="outlined"
            type={
                field.input_type === 'email'
                    ? 'email'
                    : field.input_type === 'phone'
                    ? 'tel'
                    : field.input_type === 'number'
                    ? 'number'
                    : 'text'
            }
            label={label}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            required={required}
            fullWidth
            InputLabelProps={questionLabelProps}
            multiline={field.input_type === 'textarea'}
            rows={field.input_type === 'textarea' ? 3 : undefined}
            helperText={field.field_id === 'pet_weight' ? 'Pounds' : undefined}
        />
    )
}
